import React, { useEffect, useState, useCallback } from 'react';
import { Button, Input, Table, Tag, Modal } from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';

import { BiExport } from 'react-icons/bi';
import { FaSearch } from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';
import { HiOutlineRefresh } from 'react-icons/hi';

import '../../../css/UserAdmin/Global.css';
import Footer from '../Footer'; 
import Header from '../NewHeader'; 
import SideNav from '../SideNav'; 
import ExportOptionsModal from '../ExportOptionsModal'; 

import { DateRangeFilter, isInDateRange } from '../../../utils/tableUtils'; 

dayjs.extend(utc);
dayjs.extend(isBetween);

// Define the columns available for export for this specific page
const ALL_EXPORT_COLUMNS = [
  { label: 'Timestamp', value: 'createdAt' },
  { label: 'User ID', value: 'userID' },
  { label: 'Account Name', value: 'accountName' },
  { label: 'Role', value: 'role' },
  { label: 'Action', value: 'action' },
  { label: 'Detail', value: 'detail' },
];

const SystemLogs = () => {
  // State for page layout and responsiveness
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  // State for Ant Design table data and controls
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tableFilters, setTableFilters] = useState({});
  const [sorter, setSorter] = useState({});

  // State specifically for the export functionality
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetches data and applies client-side search for the main table view
  const fetchSystemLogs = async (search = '') => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const logs = await response.json();

      const filteredLogs = logs.filter((log) => {
        const createdAtFormatted = dayjs(log.createdAt).format('MMM D, YYYY h:mm A').toLowerCase();
        return (
          (log.userID || '').toLowerCase().includes(search.toLowerCase()) ||
          (log.accountName || '').toLowerCase().includes(search.toLowerCase()) ||
          (log.role || '').toLowerCase().includes(search.toLowerCase()) ||
          (log.action || '').toLowerCase().includes(search.toLowerCase()) ||
          (log.detail || '').toLowerCase().includes(search.toLowerCase()) ||
          createdAtFormatted.includes(search.toLowerCase())
        );
      });
      const formattedData = filteredLogs.map((log, index) => ({ ...log, key: log._id || index }));
      setDataSource(formattedData);
    } catch (error) {
      console.error('Error fetching system logs:', error);
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemLogs();
  }, []);

  const handleSearch = (value) => {
    setSearchTerm(value);
    fetchSystemLogs(value);
  };

  const handleClearFilters = () => {
    setTableFilters({});
    setSorter({});
    setSearchTerm('');
    fetchSystemLogs('');
  };

  const handleFinishExport = async (values) => {
    setIsExporting(true);
    try {
      const { format, columns, dateRange, roles, actions, searchText } = values;
      
      const fullName = localStorage.getItem('fullName') || 'Unknown User';
      const requestorRole = localStorage.getItem('role') || 'Unknown Role';
      const requestorUserID = localStorage.getItem('userID') || 'Unknown ID';

      const endpoint = format === 'pdf'
        ? `${process.env.REACT_APP_API_URL}/api/admin/export/pdf/system-logs`
        : `${process.env.REACT_APP_API_URL}/api/admin/export/csv/system-logs`;

      const params = new URLSearchParams({
        userID: requestorUserID, fullName, role: requestorRole, columns: columns.join(','),
      });

      if (dateRange && dateRange.length === 2) { params.append('startDate', dateRange[0].toISOString()); params.append('endDate', dateRange[1].toISOString()); }
      if (roles && roles.length > 0) { roles.forEach(r => params.append('roles', r)); }
      if (actions && actions.length > 0) { actions.forEach(a => params.append('actions', a)); }
      if (searchText) { params.append('searchText', searchText); }

      const requestUrl = `${endpoint}?${params.toString()}`;
      
      const response = await fetch(requestUrl);
      if (!response.ok) throw new Error(`Export request failed: ${response.statusText}`);
      
      const blob = await response.blob();
      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `system-logs-report-${currentDate}.${format}`;
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setIsExportModalOpen(false);
    } catch (error) {
      console.error(`Export to ${values.format.toUpperCase()} failed:`, error);
      Modal.error({ title: 'Export Failed', content: `Could not generate the report. Please try again.` });
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    { 
      title: 'Timestamp', 
      width: 90, 
      dataIndex: 'createdAt', 
      key: 'createdAt', 
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(), 
      sortOrder: sorter.columnKey === 'createdAt' ? sorter.order : null, 
      filterDropdown: (props) => <DateRangeFilter {...props} />, 
      onFilter: (value, record) => isInDateRange(value, record.createdAt), 
      filteredValue: tableFilters.createdAt || null, 
      render: (text) => dayjs(text).isValid() ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : 'Invalid Date'
    },
    { title: 'User ID', width: 120, dataIndex: 'userID', key: 'userID', sorter: (a, b) => (a.userID || '').localeCompare(b.userID || ''), sortOrder: sorter.columnKey === 'userID' ? sorter.order : null, },
    { title: 'Account Name', width: 130, dataIndex: 'accountName', key: 'accountName', sorter: (a, b) => (a.accountName || '').localeCompare(b.accountName || ''), sortOrder: sorter.columnKey === 'accountName' ? sorter.order : null, },
    { title: 'Role', width: 120, dataIndex: 'role', key: 'role', filters: [{ text: 'Admissions (Staff)', value: 'Admissions (Staff)' }, { text: 'Registrar (Staff)', value: 'Registrar (Staff)' }, { text: 'Accounting (Staff)', value: 'Accounting (Staff)' }, { text: 'Sub-Admin', value: 'Sub-Admin' }, { text: 'Super Admin', value: 'Super Admin' }], onFilter: (value, record) => (record.role || '').includes(value), filteredValue: tableFilters.role || null, },
    { title: 'Action', width: 70, dataIndex: 'action', key: 'action', filters: [{ text: 'Logged In', value: 'Logged In' }, { text: 'Logged Out', value: 'Logged Out' }, { text: 'Login Failed', value: 'Login Failed' }, { text: 'Create', value: 'Create' }, { text: 'Update', value: 'Update' }, { text: 'Archive', value: 'Archive' }, { text: 'Unarchive', value: 'Unarchive' }, { text: 'Export', value: 'Export' }], onFilter: (value, record) => (record.action || '').includes(value), filteredValue: tableFilters.action || null, render: (action) => { const colorMap = { 'Logged In': 'green', 'Logged Out': 'volcano', 'Login Failed': 'orange', 'Create': 'blue', 'Update': 'gold', 'Archive': 'cyan', 'Unarchive': 'purple', 'Export': 'geekblue' }; return <Tag color={colorMap[action] || 'default'}>{action}</Tag>; } },
    { title: 'Detail', width: 400, dataIndex: 'detail', key: 'detail', sorter: (a, b) => (a.detail || '').localeCompare(b.detail || ''), sortOrder: sorter.columnKey === 'detail' ? sorter.order : null, },
  ];

  return (
    <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
      <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
      <div className="admin-content-wrapper">
        <div className="admin-sidenav-container">
          <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
        </div>
        <main className="admin-main-content-area">
          <div className="page-title"><p className="heading">System Logs</p></div>
          <div className="table-functions">
            <div className="left-tools">
              <Button icon={<FiFilter />} onClick={handleClearFilters}>Clear Filter</Button>
              <Button icon={<HiOutlineRefresh />} onClick={() => fetchSystemLogs(searchTerm)}>Refresh</Button>
              <Button icon={<BiExport />} onClick={() => setIsExportModalOpen(true)}>Export</Button>
            </div>
            <div className="right-tools">
              <Input placeholder="Search Log" allowClear style={{ width: 300 }} value={searchTerm} onChange={(e) => handleSearch(e.target.value)} suffix={<FaSearch style={{ color: '#aaa' }} />} />
            </div>
          </div>
          <Table style={{ width: '100%', flex: 1 }} columns={columns} dataSource={dataSource} loading={loading} scroll={{ x: true }} pagination bordered onChange={(pagination, filters, sorter) => { setTableFilters(filters); setSorter(sorter); }} />
        </main>
      </div>
      <Footer />
      <ExportOptionsModal
        visible={isExportModalOpen}
        onCancel={() => setIsExportModalOpen(false)}
        onExport={handleFinishExport}
        isLoading={isExporting}
        exportColumns={ALL_EXPORT_COLUMNS}
        textFilter={{ name: 'searchText', label: 'Filter by User ID or Account Name', placeholder: 'Enter a user ID or name...' }}
        filterOptions={[
          { key: 'roles', name: 'roles', label: 'Role', options: columns.find(c => c.key === 'role')?.filters || [] },
          { key: 'actions', name: 'actions', label: 'Action', options: columns.find(c => c.key === 'action')?.filters || [] }
        ]}
      />
    </div>
  );
}

export default SystemLogs;